import React from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { isValidNumber } from 'libphonenumber-js';

import { Input as BaseInput, Form, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';
import { FormLabel } from '../../../../components';
import Gender from './Gender';
import EmailField from './EmailField';
import PhoneField from './PhoneField';
import Calendar from '../../../appointments/containers/form/Calendar';
import Checkbox from '../../../../components/Checkbox';

import styles from '../../../../styles';

const Label = styled(FormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const Input = styled(BaseInput)`
    & input {
        background-color: ${props => props.readOnly ? '#fffaf3' : '#fff'} !important;
    }
`;

const intlMessages = defineMessages({
    firstNameError: {
        id: 'pages.clients.ClientPage.clients.firstNameError',
        defaultMessage: 'Enter client first name',
    },
    genderError: {
        id: 'pages.clients.ClientPage.clients.genderError',
        defaultMessage: 'Please select client gender',
    },
    phoneError: {
        id: 'pages.clients.ClientPage.clients.phoneError',
        defaultMessage: 'Invalid phone number.',
    },
    noCountryCodeError: {
        id: 'pages.clients.ClientPage.clients.noCountryCodeError',
        defaultMessage: 'Phone number should start with country code',
    },
    clientExistsMessage: {
        id: 'pages.clients.form.ClientForm.clientExistsMessage',
        defaultMessage: 'already exists in our system, you can add it to your salon',
    },
});

function ClientForm({ readOnly, userLookupStatus, userLookupUser, onUserLookup, resetUserLookup, intl }) {
    const { control, formState: { errors } } = useFormContext();

    const validatePhone = value => {
        if (!value) return true;
        if (value[0] !== '+') return intl.formatMessage(intlMessages.noCountryCodeError);
        if (!isValidNumber(value)) return intl.formatMessage(intlMessages.phoneError);
        return true;
    };

    return (
        <Form error={!!errors.root}>
            {userLookupUser && (
                <Message info>
                    <Icon name="info" />
                    <strong>
                        {userLookupUser.firstName} {userLookupUser.lastName}
                    </strong>{' '}
                    <FormattedMessage {...intlMessages.clientExistsMessage} />
                </Message>
            )}
            {errors.root && (
                <Message error>
                    <Icon name="warning sign" />
                    {errors.root.message}
                </Message>
            )}
            <Form.Group widths="equal">
                <Form.Field error={!!errors.phone}>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.phone"
                        defaultMessage="Phone"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="phone"
                        control={control}
                        rules={{ validate: validatePhone }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <PhoneField
                                {...field}
                                onChange={(e, data) => field.onChange(data?.value ?? e)}
                                onBlur={() => {
                                    field.onBlur();
                                    if (field.value) {
                                        onUserLookup?.({ phone: field.value.replace(/[^+\d]+/g, ''), role: 'CLIENT' });
                                    } else {
                                        resetUserLookup?.();
                                    }
                                }}
                                userLookupStatus={userLookupStatus}
                                disabled={readOnly}
                            />
                        )}
                    />
                    {errors.phone && (
                        <BaseLabel color="red" pointing basic>{errors.phone.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field error={!!errors.email}>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.email"
                        defaultMessage="E-mail"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <EmailField
                                {...field}
                                onBlur={() => {
                                    field.onBlur();
                                    if (field.value) {
                                        onUserLookup?.({ email: field.value, role: 'CLIENT' });
                                    } else {
                                        resetUserLookup?.();
                                    }
                                }}
                                userLookupStatus={userLookupStatus}
                                disabled={readOnly}
                            />
                        )}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.firstName}>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.firstName"
                        defaultMessage="First Name"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.firstNameError) }}
                        render={({ field }) => (
                            <Input {...field} readOnly={readOnly} fluid />
                        )}
                    />
                    {errors.firstName && (
                        <BaseLabel color="red" pointing basic>{errors.firstName.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.lastName"
                        defaultMessage="Last Name"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                            <Input {...field} readOnly={readOnly} fluid />
                        )}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.notes"
                        defaultMessage="Notes"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => <Input {...field} fluid />}
                    />
                </Form.Field>
                <Form.Field>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.birthday"
                        defaultMessage="Birthday"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="birthday"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Calendar
                                value={value}
                                onChange={onChange}
                                format="dd.MM.yyyy"
                                dayPickerProps={{ isDayPickerInput: true }}
                                width={4}
                            />
                        )}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.gender}>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.gender"
                        defaultMessage="Gender"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="gender"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.genderError) }}
                        render={({ field: { value, onChange } }) => (
                            <Gender
                                value={value}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                                disabled={readOnly}
                            />
                        )}
                    />
                    {errors.gender && (
                        <BaseLabel color="red" pointing basic>{errors.gender.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.blocked"
                        defaultMessage="Block client"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="blocked"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Checkbox
                                checked={!!value}
                                onChange={(e, data) => onChange(data?.checked ?? false)}
                            />
                        )}
                    />
                </Form.Field>
            </Form.Group>
        </Form>
    );
}

export default injectIntl(ClientForm);
